import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlag } from '@core/dev-flags-types';
import { HttpApiService } from '@core/http-legacy';
import type { PaginationMeta } from '@core/http-types';
import { JobBasic, OfferItem, OfferKind, OfferStatus } from '@core/offers-types';

import { MapperService } from '../../services/mapper.service';
import { OfferScope } from '../../types';

@Injectable({
  providedIn: 'root',
})
export class OffersByCompanyHttpService {
  private readonly httpApi = inject(HttpApiService);
  private readonly store = inject(Store);

  private readonly hasForceNewOffersEndpointFlag = this.store.selectSignal(
    DevFlagSelectors.isEnabled(DevFlag.ForceNewOffersEndpointFlag),
  );

  public fetchOne$(
    id: string,
    page: number,
  ): Observable<{ offers: JobBasic[]; id: string; meta?: PaginationMeta }> {
    if (this.hasForceNewOffersEndpointFlag()) {
      const url = `${this.httpApi.appConfig.apiUrl}c/${id}/offers`;
      return this.httpApi
        .get(
          url,
          { include: ['location_ids', 'counters', 'work_model', 'issues'], page },
          { skipURLBuild: true },
        )
        .pipe(
          filterBoolean,
          map(response => ({
            id,
            offers: response.offers
              .filter((job: JobBasic) => job.status !== OfferStatus.Archived)
              .map((job: JobBasic) => ({ ...job, kind: OfferKind.Job })),
            meta: response.meta,
          })),
        );
    } else {
      const url = `${this.httpApi.appConfig.apiUrl}c/${id}/offers?scope=${OfferScope.Active}`;

      return this.httpApi.get(url, { page }, { skipURLBuild: true }).pipe(
        map(response => ({
          offers: response.offers.map((job: OfferItem) =>
            MapperService.mapOfferItemToJobBasic(job),
          ),
          id,
        })),
      );
    }
  }
}
